import { Link } from "react-router-dom";
import {
    ArrowRight, Calculator, Coins, Trees, Banknote, Handshake, ShieldCheck,
    Building, Network, Eye, Lock, Sparkles, Calendar, ChevronRight,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const COMPARISON = [
    {
        kicker: "Outright sale",
        ranges: [
            { label: "Cash to landowner", v: "Once. Done." },
            { label: "Future upside", v: "0%" },
            { label: "Risk", v: "None — no project risk" },
            { label: "Mataqali / family in 10 years", v: "Same income from a different lease" },
        ],
        accent: "muted",
        icon: Banknote,
    },
    {
        kicker: "Joint Venture",
        ranges: [
            { label: "Cash to landowner", v: "Goodwill payment + quarterly distributions" },
            { label: "Future upside", v: "15–30% lifelong equity" },
            { label: "Risk", v: "Shared with capital partner" },
            { label: "Mataqali / family in 10 years", v: "+138% on average vs sale" },
        ],
        accent: "accent",
        icon: Handshake,
    },
];

const STEPS = [
    { n: "01", t: "Landowner brings the land", d: "Native lease, freehold, or crown lease — we work with all three.", icon: Trees },
    { n: "02", t: "Capital partner brings the cash", d: "Vetted foreign investor we've already cleared through our 12-step compliance pipeline.", icon: Coins },
    { n: "03", t: "We structure the JV", d: "Mataqali equity protected (15–30%), reserved-matters seat, escrowed distributions.", icon: Building },
    { n: "04", t: "We handle every approval", d: "Investor Permit, FIRCA, RBF, council planning, EIA, tax incentives — all of it.", icon: Network },
    { n: "05", t: "Quarterly distributions begin", d: "Released from escrow to the landowner's bank account on schedule.", icon: Banknote },
    { n: "06", t: "Equity stays. For the life of the asset.", d: "When the project sells, the landowner gets their pro-rata share.", icon: Sparkles },
];

const FAQS = [
    {
        q: "Does the landowner give up their land?",
        a: "No. The Mataqali / family unit retains their lease or freehold title. The JV is a structured ownership of the income stream from what's built on it — not a sale of the underlying land. At project end, the asset is sold and proceeds split pro-rata.",
    },
    {
        q: "What if the project fails?",
        a: "JV equity is protected by reserved-matters provisions in the deed. The landowner is never asked to put cash in. If the capital partner fails to perform, the asset and improvements typically revert to the landowner per the JV agreement.",
    },
    {
        q: "Why not just lease it again?",
        a: "Leases are paid at fixed rents that reset every 5–10 years. JVs participate in project upside — tourism, hospitality, and renewable-energy projects routinely return many multiples of what a flat lease pays out.",
    },
    {
        q: "Who actually pays for everything?",
        a: "The capital partner contributes 100% of capex. The landowner contributes the land itself as their equity in. We charge a structuring fee at signing and a small ongoing administrative fee.",
    },
];

export default function ExplainJV() {
    return (
        <div data-testid="explain-jv-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-20 lg:py-28">
                <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-coral/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Calendar className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            For agents · 60-second landowner explainer
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-6">
                        Don't sell the land. <span className="italic text-turquoise">Partner with it.</span>
                    </h1>
                    <p className="font-app text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-3xl mb-8">
                        Most landowners who can't afford to develop their land just lease it out. They're leaving 100%+ on the
                        table. Joint Ventures keep them in the asset for life — quarterly distributions, retained equity, full
                        protection. This page walks them through it in a minute.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                        <Button asChild size="lg" data-testid="explain-jv-calc-cta" className="bg-coral hover:bg-coral-deep text-white font-app">
                            <Link to="/agents/jv-calculator">
                                <Calculator className="h-4 w-4 mr-2" />
                                Open the JV Calculator <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" data-testid="explain-jv-console-cta" className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white font-app">
                            <Link to="/agent-console">
                                Open Agent Console
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* COMPARISON */}
            <section className="py-20 lg:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        The 30-second version
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-10 text-foreground leading-tight">
                        Two ways to monetise a piece of land in Fiji.
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {COMPARISON.map((c) => {
                            const Icon = c.icon;
                            const isAccent = c.accent === "accent";
                            return (
                                <Card
                                    key={c.kicker}
                                    data-testid={`explain-jv-compare-${c.kicker.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`p-6 lg:p-7 ${
                                        isAccent
                                            ? "bg-fiji-green/5 dark:bg-fiji-green/10 border-fiji-green/30"
                                            : "bg-card border-border"
                                    }`}
                                >
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className={`h-10 w-10 rounded-md grid place-items-center ${
                                            isAccent ? "bg-fiji-green/15" : "bg-muted"
                                        }`}>
                                            <Icon className={`h-5 w-5 ${isAccent ? "text-fiji-green" : "text-muted-foreground"}`} />
                                        </div>
                                        <div className="font-display text-lg font-bold text-foreground">{c.kicker}</div>
                                    </div>
                                    <div className="space-y-3">
                                        {c.ranges.map((r) => (
                                            <div key={r.label} className="flex items-baseline justify-between gap-3 pb-3 border-b border-border last:border-0">
                                                <span className="font-app text-xs text-muted-foreground">{r.label}</span>
                                                <span className={`font-app text-sm tabular-nums text-right ${
                                                    isAccent ? "text-fiji-green font-semibold" : "text-foreground/85"
                                                }`}>
                                                    {r.v}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS — 6 steps */}
            <section className="py-20 lg:py-24 bg-secondary/40 dark:bg-card/30 border-y border-border">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        How a Joint Venture works
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-10 text-foreground leading-tight">
                        Six steps. <span className="italic text-coral">No upfront cost to landowner.</span>
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {STEPS.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Card key={s.n} data-testid={`explain-jv-step-${s.n}`} className="p-5 bg-card hover:border-coral/40 transition">
                                    <div className="flex items-baseline gap-3 mb-3">
                                        <div className="font-display text-3xl font-bold text-coral tabular-nums">{s.n}</div>
                                        <Icon className="h-4 w-4 text-fiji-green" />
                                    </div>
                                    <div className="font-display text-sm font-bold text-foreground mb-1.5">{s.t}</div>
                                    <p className="font-app text-xs text-muted-foreground leading-relaxed">{s.d}</p>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PROTECTIONS */}
            <section className="py-20 lg:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        Built so the landowner is never exposed
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-10 text-foreground leading-tight">
                        What's actually protecting the Mataqali?
                    </h2>
                    <Card className="p-6 lg:p-8 bg-coral/5 dark:bg-coral/10 border-coral/30 mb-6">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-md bg-coral/15 grid place-items-center shrink-0">
                                <Lock className="h-5 w-5 text-coral" />
                            </div>
                            <div>
                                <div className="font-display text-lg font-bold text-foreground mb-2">
                                    The platform routes every conversation
                                </div>
                                <p className="font-app text-sm text-foreground/85 leading-relaxed">
                                    Bula Real Estate Fiji is the official intermediary. The landowner never speaks directly to
                                    the foreign investor — every message, document, and offer goes through us, with full audit
                                    trail. This protects the Mataqali from unsolicited approach and ensures every interaction
                                    is logged, vetted, and routed.
                                </p>
                            </div>
                        </div>
                    </Card>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {[
                            { t: "Equity is non-dilutable", d: "Reserved-matters protections written into the JV deed.", icon: Trees },
                            { t: "Distributions held in escrow", d: "Released to the landowner's bank account on schedule.", icon: Banknote },
                            { t: "Independent annual audit", d: "Audit-ready documentation throughout the partnership.", icon: ShieldCheck },
                            { t: "TLTB observer rights (where applicable)", d: "Standing observer seat on JV board for native-lease projects.", icon: Eye },
                        ].map((g) => {
                            const Icon = g.icon;
                            return (
                                <Card key={g.t} className="p-5 bg-card flex items-start gap-3">
                                    <Icon className="h-5 w-5 text-fiji-green mt-0.5 shrink-0" />
                                    <div>
                                        <div className="font-display text-sm font-bold text-foreground mb-1">{g.t}</div>
                                        <p className="font-app text-xs text-muted-foreground leading-relaxed">{g.d}</p>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section className="py-20 lg:py-24 bg-secondary/40 dark:bg-card/30 border-y border-border">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        Most common landowner questions
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-10 text-foreground leading-tight">
                        The fast answers.
                    </h2>
                    <div className="space-y-4">
                        {FAQS.map((f, i) => (
                            <Card key={f.q} data-testid={`explain-jv-faq-${i}`} className="p-5 bg-card">
                                <div className="font-display text-base font-bold text-foreground mb-2 flex items-start gap-2">
                                    <ChevronRight className="h-4 w-4 text-coral mt-1 shrink-0" />
                                    <span>{f.q}</span>
                                </div>
                                <p className="font-app text-sm text-muted-foreground leading-relaxed pl-6">{f.a}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-20 lg:py-24 bg-ocean-deep-grad text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-3xl lg:text-5xl font-bold mb-5 leading-tight">
                        Run the numbers <span className="italic text-turquoise">in front of them</span>.
                    </h2>
                    <p className="font-app text-base lg:text-lg text-white/85 leading-relaxed mb-8">
                        Plug in their asking price. Show them what they'd net on a sale. Show them what 25% lifelong equity
                        looks like over 10 years. Most landowners change their mind on the spot.
                    </p>
                    <Button asChild size="lg" data-testid="explain-jv-final-calc-cta" className="bg-coral hover:bg-coral-deep text-white font-app">
                        <Link to="/agents/jv-calculator">
                            <Calculator className="h-4 w-4 mr-2" />
                            Open the JV Calculator <ArrowRight className="h-4 w-4 ml-2" />
                        </Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}
